import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Hash,
  Layers,
  MapPin,
  Plus,
  Route as RouteIcon,
  User as UserIcon,
} from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  PageHeader,
  TableWrap,
  controlClasses,
  type BadgeTone,
} from "../../components/ui";
import { listarArranques, type ArranqueRead } from "../arranques/api";
import {
  asignarArranques,
  compactarRuta,
  listarArranquesDeRuta,
  obtenerRuta,
  reordenarArranques,
  type ArranqueEnRuta,
  type ItemReorden,
} from "./api";

export function RutaDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const rutaId = Number(id);
  const qc = useQueryClient();
  const toast = useToast();

  const [asignarOpen, setAsignarOpen] = useState(false);
  const [compactarConfirm, setCompactarConfirm] = useState(false);

  const rutaQ = useQuery({
    queryKey: ["rutas", rutaId, "detalle"],
    queryFn: () => obtenerRuta(rutaId),
    enabled: Number.isFinite(rutaId),
  });
  const arranquesQ = useQuery({
    queryKey: ["rutas", rutaId, "arranques"],
    queryFn: () => listarArranquesDeRuta(rutaId),
    enabled: Number.isFinite(rutaId),
  });

  const compactarMut = useMutation({
    mutationFn: () => compactarRuta(rutaId),
    onSuccess: () => {
      toast.success("Orden compactado (10, 20, 30…)");
      setCompactarConfirm(false);
      void qc.invalidateQueries({ queryKey: ["rutas", rutaId, "arranques"] });
    },
    onError: (err) => {
      setCompactarConfirm(false);
      toast.error(err instanceof ApiError ? err.message : "No se pudo compactar");
    },
  });

  const reordenarMut = useMutation({
    mutationFn: (items: ItemReorden[]) => reordenarArranques(rutaId, items),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["rutas", rutaId, "arranques"] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "No se pudo reordenar");
    },
  });

  if (rutaQ.isPending) {
    return <Cargando />;
  }
  if (rutaQ.isError || !rutaQ.data) {
    return (
      <div className="space-y-4">
        <PageHeader title="Ruta" volver={{ etiqueta: "Rutas", ruta: "/rutas" }} />
        <ErrorCarga
          error={rutaQ.error}
          mensaje="Ruta no encontrada."
          onReintentar={() => void rutaQ.refetch()}
        />
      </div>
    );
  }
  const ruta = rutaQ.data;
  const arranques = arranquesQ.data ?? [];

  function moverArriba(idx: number): void {
    if (idx === 0 || arranques.length < 2) return;
    const nuevo = [...arranques];
    [nuevo[idx - 1], nuevo[idx]] = [nuevo[idx]!, nuevo[idx - 1]!];
    enviarOrden(nuevo);
  }
  function moverAbajo(idx: number): void {
    if (idx >= arranques.length - 1) return;
    const nuevo = [...arranques];
    [nuevo[idx], nuevo[idx + 1]] = [nuevo[idx + 1]!, nuevo[idx]!];
    enviarOrden(nuevo);
  }
  function enviarOrden(lista: ArranqueEnRuta[]): void {
    const items: ItemReorden[] = lista.map((a, i) => ({
      arranque_id: a.id,
      orden: (i + 1) * 10,
    }));
    reordenarMut.mutate(items);
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={`Ruta ${ruta.nombre}`}
        icon={RouteIcon}
        subtitle={<span className="font-mono uppercase">{ruta.codigo}</span>}
        volver={{ etiqueta: "Rutas", ruta: "/rutas" }}
      />

      <Card>
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-600">
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3 opacity-60" />
                Sector {ruta.sector_codigo ? `${ruta.sector_codigo} — ` : ""}
                {ruta.sector_nombre}
              </span>
              <span className="inline-flex items-center gap-1">
                <UserIcon className="h-3 w-3 opacity-60" />
                {ruta.operador_nombre ?? (
                  <span className="italic">sin operador asignado</span>
                )}
              </span>
              <span className="inline-flex items-center gap-1">
                <Hash className="h-3 w-3 opacity-60" />
                Día lectura{" "}
                <strong>
                  {ruta.dia_lectura ?? `${ruta.dia_lectura_efectivo}`}
                </strong>
                {ruta.dia_lectura == null && (
                  <span className="text-neutral-500">(hereda)</span>
                )}
              </span>
              <span className="inline-flex items-center gap-1">
                <Layers className="h-3 w-3 opacity-60" />
                {ruta.cantidad_arranques} arranque
                {ruta.cantidad_arranques !== 1 ? "s" : ""}
              </span>
              {!ruta.activa && <Badge tone="slate">Inactiva</Badge>}
            </div>
            {ruta.descripcion && (
              <p className="mt-2 text-sm text-neutral-600">{ruta.descripcion}</p>
            )}
          </div>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-neutral-900">
          Arranques en orden
        </h3>
        <span className="text-xs text-neutral-500">({arranques.length})</span>
        <div className="ml-auto flex items-center gap-2">
          <Button icon={Plus} onClick={() => setAsignarOpen(true)}>
            Asignar arranques
          </Button>
          <Button
            variant="secondary"
            icon={ChevronDown}
            onClick={() => setCompactarConfirm(true)}
            disabled={compactarMut.isPending || arranques.length === 0}
          >
            Compactar orden
          </Button>
        </div>
      </div>

      {arranquesQ.isPending && <Cargando texto="Cargando arranques…" />}

      {arranques.length === 0 && !arranquesQ.isPending && (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-6 text-center">
          <p className="text-sm text-neutral-600">
            Esta ruta no tiene arranques. Toca{" "}
            <strong>Asignar arranques</strong> para agregar los del sector.
          </p>
        </div>
      )}

      {arranques.length > 0 && (
        <TableWrap label="Arranques de la ruta">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-2 py-2 text-right w-12">Pos</th>
                <th scope="col" className="px-3 py-2 text-left">Código</th>
                <th scope="col" className="px-3 py-2 text-left">Dirección</th>
                <th scope="col" className="px-3 py-2 text-left">N° casa</th>
                <th scope="col" className="px-3 py-2 text-left">Referencia</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right w-32">Mover</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {arranques.map((a, idx) => (
                <tr key={a.id} className="hover:bg-neutral-50">
                  <td className="px-2 py-2 text-right tabular-nums text-neutral-500">
                    {a.orden_ruta ?? "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {a.codigo_arranque ?? `#${a.id}`}
                  </td>
                  <td className="px-3 py-2 text-neutral-700">{a.direccion}</td>
                  <td className="px-3 py-2 text-neutral-600">
                    {a.numero_casa ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-neutral-500 max-w-xs truncate">
                    {a.referencia ?? "—"}
                  </td>
                  <td className="px-3 py-2">
                    <EstadoBadge estado={a.estado} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        icon={ArrowUp}
                        aria-label="Subir"
                        title="Subir"
                        onClick={() => moverArriba(idx)}
                        disabled={idx === 0 || reordenarMut.isPending}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconOnly
                        icon={ArrowDown}
                        aria-label="Bajar"
                        title="Bajar"
                        onClick={() => moverAbajo(idx)}
                        disabled={
                          idx === arranques.length - 1 || reordenarMut.isPending
                        }
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {asignarOpen && (
        <AsignarArranquesModal
          rutaId={rutaId}
          sectorId={ruta.sector_id}
          arranquesYaEnRuta={arranques.map((a) => a.id)}
          onClose={() => setAsignarOpen(false)}
        />
      )}

      {compactarConfirm && (
        <ConfirmDialog
          open
          tone="default"
          title="Compactar el orden"
          description="Renumera los arranques a 10, 20, 30… manteniendo el orden actual. Sirve para dejar huecos parejos entre posiciones."
          confirmLabel="Compactar"
          loading={compactarMut.isPending}
          onCancel={() => setCompactarConfirm(false)}
          onConfirm={() => compactarMut.mutate()}
        />
      )}
    </div>
  );
}

const ESTADO_TONE: Record<string, BadgeTone> = {
  Activo: "emerald",
  Retirado: "slate",
  Inactivo: "amber",
};

function EstadoBadge({ estado }: { estado: string }): JSX.Element {
  // Estados de arranque (físicos): Activo / Retirado / Inactivo. El corte
  // por morosidad es estado del contrato, no del arranque.
  return <Badge tone={ESTADO_TONE[estado] ?? "amber"}>{estado}</Badge>;
}

// ────────────────────────────────────────────────────────────────────────
// Modal: asignar arranques
// ────────────────────────────────────────────────────────────────────────

function AsignarArranquesModal({
  rutaId,
  sectorId,
  arranquesYaEnRuta,
  onClose,
}: {
  rutaId: number;
  sectorId: number;
  arranquesYaEnRuta: number[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [seleccionados, setSeleccionados] = useState<Set<number>>(new Set());
  const [filtro, setFiltro] = useState("");

  const todosArranquesQ = useQuery({
    queryKey: ["arranques", { sector_id: sectorId }],
    queryFn: () => listarArranques(sectorId),
  });

  const candidatos = useMemo(() => {
    const data = todosArranquesQ.data ?? [];
    const enRuta = new Set(arranquesYaEnRuta);
    let res = data.filter((a) => !enRuta.has(a.id));
    if (filtro.trim()) {
      const f = filtro.trim().toLowerCase();
      res = res.filter(
        (a) =>
          (a.codigo_arranque ?? "").toLowerCase().includes(f) ||
          a.direccion.toLowerCase().includes(f) ||
          (a.numero_casa ?? "").toLowerCase().includes(f) ||
          (a.referencia ?? "").toLowerCase().includes(f),
      );
    }
    return res;
  }, [todosArranquesQ.data, arranquesYaEnRuta, filtro]);

  const asignarMut = useMutation({
    mutationFn: (ids: number[]) => asignarArranques(rutaId, ids),
    onSuccess: (_, ids) => {
      toast.success(
        `${ids.length} arranque${ids.length !== 1 ? "s" : ""} asignado${
          ids.length !== 1 ? "s" : ""
        } a la ruta`,
      );
      void qc.invalidateQueries({ queryKey: ["rutas", rutaId, "arranques"] });
      void qc.invalidateQueries({ queryKey: ["rutas", rutaId, "detalle"] });
      void qc.invalidateQueries({ queryKey: ["rutas"] });
      onClose();
    },
    onError: (err) => {
      toast.error(
        err instanceof ApiError ? err.message : "No se pudieron asignar",
      );
    },
  });

  function toggle(id: number): void {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleTodos(): void {
    if (seleccionados.size === candidatos.length) {
      setSeleccionados(new Set());
    } else {
      setSeleccionados(new Set(candidatos.map((c) => c.id)));
    }
  }

  function submit(): void {
    if (seleccionados.size === 0) return;
    // Mantener el orden visual del filtro actual.
    const ids = candidatos
      .filter((c) => seleccionados.has(c.id))
      .map((c) => c.id);
    asignarMut.mutate(ids);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Asignar arranques a la ruta"
      subtitle={`${candidatos.length} disponible${candidatos.length !== 1 ? "s" : ""} en este sector`}
      icon={Plus}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={asignarMut.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={asignarMut.isPending || seleccionados.size === 0}
          >
            {asignarMut.isPending
              ? "Asignando…"
              : `Asignar ${seleccionados.size}`}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <input
          type="text"
          aria-label="Buscar arranques"
          placeholder="Buscar por código / dirección / N° casa / referencia…"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          className={controlClasses}
        />

        {todosArranquesQ.isPending && (
          <Cargando inline texto="Cargando arranques…" />
        )}
        {todosArranquesQ.isError && (
          <ErrorCarga
            error={todosArranquesQ.error}
            mensaje="Error al cargar arranques."
            onReintentar={() => void todosArranquesQ.refetch()}
          />
        )}

        {candidatos.length === 0 && !todosArranquesQ.isPending && (
          <p className="text-sm text-neutral-500 italic py-4 text-center">
            {filtro
              ? "Sin resultados para tu búsqueda."
              : "No quedan arranques disponibles en este sector. Todos ya están asignados a alguna ruta."}
          </p>
        )}

        {candidatos.length > 0 && (
          <>
            <Button variant="link" size="sm" onClick={toggleTodos}>
              {seleccionados.size === candidatos.length
                ? "Quitar selección"
                : "Seleccionar todos"}
            </Button>
            <div className="max-h-96 overflow-auto rounded-md border border-neutral-200">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500 sticky top-0">
                  <tr>
                    <th scope="col" className="px-2 py-2 w-8"></th>
                    <th scope="col" className="px-3 py-2 text-left">Código</th>
                    <th scope="col" className="px-3 py-2 text-left">Dirección</th>
                    <th scope="col" className="px-3 py-2 text-left">Ruta actual</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {candidatos.map((a) => (
                    <FilaCandidato
                      key={a.id}
                      arranque={a}
                      seleccionado={seleccionados.has(a.id)}
                      onToggle={() => toggle(a.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-neutral-500 italic">
              Si seleccionas un arranque que ya está en otra ruta, se mueve a
              esta. Si no tiene ruta, se asigna por primera vez.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

function FilaCandidato({
  arranque,
  seleccionado,
  onToggle,
}: {
  arranque: ArranqueRead;
  seleccionado: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <tr
      onClick={onToggle}
      className={`cursor-pointer ${
        seleccionado ? "bg-brand-50" : "hover:bg-neutral-50"
      }`}
    >
      <td className="px-2 py-2 text-center">
        <input
          type="checkbox"
          aria-label={`Seleccionar ${arranque.codigo_arranque ?? arranque.id}`}
          checked={seleccionado}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="rounded border-neutral-450 text-brand-600 focus:ring-brand-500"
        />
      </td>
      <td className="px-3 py-2 font-mono text-xs">
        {arranque.codigo_arranque ?? `#${arranque.id}`}
      </td>
      <td className="px-3 py-2">
        <div className="text-neutral-700">
          {arranque.direccion}
          {arranque.numero_casa && (
            <span className="ml-1 text-neutral-500">{arranque.numero_casa}</span>
          )}
        </div>
        {arranque.referencia && (
          <div className="text-xs text-neutral-500 truncate max-w-md">
            {arranque.referencia}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-neutral-500">
        {arranque.ruta_id ? (
          <span title={`Está en ruta #${arranque.ruta_id} — se moverá`}>
            En ruta #{arranque.ruta_id}
          </span>
        ) : (
          <span className="italic text-neutral-500">Sin ruta</span>
        )}
      </td>
    </tr>
  );
}
